import React, { useEffect, useRef, useState } from "react";
import { TITLE_BAR_LEFT, TITLE_BAR_MAX_WIDTH } from "./editorTitleBarLayout";

/**
 * Title chip for the current cloud file, in the top-LEFT column of the editor,
 * immediately right of the ☰ menu button (it used to be centred, on top of the
 * toolbar — see editorTitleBarLayout).
 *
 * Click to rename inline: Enter/blur commits, Escape cancels, an empty title
 * reverts to the previous one. Commit is delegated to the parent (which calls
 * filesService); the chip reflects the new title immediately.
 */
export const FileTitleChip: React.FC<{
  title: string;
  onRename?: (title: string) => void;
}> = ({ title, onRename }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Keep the draft in sync when the title changes from outside (e.g. reload).
    if (!isEditing) {
      setDraft(title);
    }
  }, [title, isEditing]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  const commit = () => {
    const next = draft.trim();
    if (next && next !== title) {
      onRename?.(next);
    } else {
      // Empty (or unchanged) reverts to the current title.
      setDraft(title);
    }
    setIsEditing(false);
  };

  const cancel = () => {
    setDraft(title);
    setIsEditing(false);
  };

  const baseStyle: React.CSSProperties = {
    position: "absolute",
    top: "1rem",
    left: TITLE_BAR_LEFT,
    zIndex: 5,
    // Width-capped so the title can never reach the centred toolbar.
    maxWidth: TITLE_BAR_MAX_WIDTH,
    padding: "0.35rem 0.85rem",
    borderRadius: "0.5rem",
    background: "var(--island-bg-color, #fff)",
    boxShadow: "var(--shadow-island, 0 1px 4px rgba(0,0,0,0.15))",
    fontSize: "0.9rem",
    fontWeight: 600,
    color: "var(--text-primary-color, #1b1b1f)",
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        className="file-title-chip file-title-chip--editing"
        data-testid="file-title-chip-input"
        aria-label="Rename file"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            commit();
          } else if (event.key === "Escape") {
            event.preventDefault();
            cancel();
          }
        }}
        style={{ ...baseStyle, border: "1px solid #6965db", outline: "none" }}
      />
    );
  }

  return (
    <button
      type="button"
      className="file-title-chip"
      // The title ellipsises in the narrow column, so always surface it in full.
      title={onRename ? `${title} — click to rename` : title}
      onClick={() => onRename && setIsEditing(true)}
      data-testid="file-title-chip"
      style={{
        ...baseStyle,
        cursor: onRename ? "text" : "default",
        border: "none",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        textAlign: "left",
      }}
    >
      {title}
    </button>
  );
};

export default FileTitleChip;
